import { useQuery } from "@hooks/useQuery";
import { Tabs } from "antd";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Loader } from "~/Components";
import { DynamicForm, FormConfig } from "~/Components/DynamicForm/DynamicForm";
import { useNotification } from "~/Hooks/useNotification";
import { magpCMSApi, post, put } from "~/Services";
import { magpHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { useDocumentDetails } from "../../../ApprovalManagement/Drilldown.Hooks";
import { useProcurementPlanning } from "../Manual.hook";
import { fields } from "../manualConstants";
import { ProcurementPlanningProcessFlowChart } from "./ProcurementPlanningProcessFlowChart";

export const ProcurementPlanning: React.FC = (): JSX.Element => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [tabsData, setTabsData] = useState<any[]>([]);
  const [subTabsData, setSubTabsData] = useState<any[]>([]);
  const [listId, setListId] = useState("");
  const [toggleId, setToggleId] = useState("");
  const approveQuery = new URLSearchParams(location.search);
  const approveId = approveQuery.get("id");

  const {
    data: procurementPlanningData,
    isLoading: isLoadingProcurementPlanning,
    refetch: refetchProcurementPlanning,
  } = useProcurementPlanning(listId || "15", toggleId || "127");
  const {
    data: procurementPlanningDataById,
    isLoading: isLoadingProcurementPlanningById,
    refetch: refetchData,
  } = useDocumentDetails(query?.get("id") || "");
  useEffect(() => {
    if (approveId) {
      return;
    }

    const thirdChildActiveTab = query.get("thirdChildActiveTab");
    if (query.get("activeTab") === "procurementPlanning" && query.get("subActiveTab") === "businessCase") {
      navigate(
        `/dedicated/magp/manual?activeTab=procurementPlanning&subActiveTab=businessCase&thirdChildActiveTab=${
          thirdChildActiveTab ?? "businessCaseOverview"
        }`,
        {
          replace: true,
        }
      );

      if (thirdChildActiveTab === "businessCaseOverview") {
        setListId("15");
        setToggleId("127");
      } else if (thirdChildActiveTab === "businessCasePolicies") {
        setListId("15");
        setToggleId("128");
      } else if (thirdChildActiveTab === "businessCaseProcessFlowChart") {
        setListId("15");
        setToggleId("129");
      } else {
        setListId("15");
        setToggleId("127");
      }
      // refetchData();
    }
    if (query.get("activeTab") === "procurementPlanning" && query.get("subActiveTab") === "annualProcurementPlanning") {
      navigate(
        `/dedicated/magp/manual?activeTab=procurementPlanning&subActiveTab=annualProcurementPlanning&thirdChildActiveTab=${
          thirdChildActiveTab ?? "annualProcurementPlanningOverview"
        }`,
        {
          replace: true,
        }
      );
      if (thirdChildActiveTab === "annualProcurementPlanningOverview") {
        setListId("16");
        setToggleId("142");
      } else if (thirdChildActiveTab === "annualProcurementPlanningPolicies") {
        setListId("16");
        setToggleId("143");
      } else if (thirdChildActiveTab === "annualProcurementPlanningProcessFlowChart") {
        setListId("16");
        setToggleId("144");
      } else {
        setListId("16");
        setToggleId("142");
      }
      // refetchData();
    }
    if (query.get("activeTab") === "procurementPlanning" && query.get("subActiveTab") === "strategyPaper") {
      navigate(
        `/dedicated/magp/manual?activeTab=procurementPlanning&subActiveTab=strategyPaper&thirdChildActiveTab=${
          thirdChildActiveTab ?? "strategyPaperOverview"
        }`,
        {
          replace: true,
        }
      );
      if (thirdChildActiveTab === "strategyPaperOverview") {
        setListId("17");
        setToggleId("145");
      } else if (thirdChildActiveTab === "strategyPaperPolicies") {
        setListId("17");
        setToggleId("146");
      } else if (thirdChildActiveTab === "strategyPaperProcessFlowChartDirectAppointment") {
        setListId("17");
        setToggleId("177");
      } else if (thirdChildActiveTab === "strategyPaperProcessFlowChartOpenTenderInvitationToBid") {
        setListId("17");
        setToggleId("225");
      } else {
        setListId("17");
        setToggleId("145");
      }
      // refetchData();
    }
  }, [query.get("activeTab"), query.get("subActiveTab")]);
  useEffect(() => {
    refetchProcurementPlanning();
  }, []);
  useEffect(() => {
    if (listId && toggleId) {
      refetchProcurementPlanning();
    }
  }, [listId, toggleId]);
  useEffect(() => {
    if (query?.get("id")) {
      refetchData();
    }
  }, [query?.get("id")]);

  const ProcurementPlanningFormConfig: FormConfig = {
    initialValues: {
      title: procurementPlanningDataById?.payload
        ? decodeHTML(procurementPlanningDataById?.payload?.title)
        : decodeHTML(procurementPlanningData?.[0]?.title),
      field_procurement_description_en: procurementPlanningDataById?.payload
        ? decodeHTML(procurementPlanningDataById?.payload?.field_procurement_description_en?.value)
        : decodeHTML(procurementPlanningData?.[0]?.field_procurement_description_en),
      field_procurement_description_ms: procurementPlanningDataById?.payload
        ? decodeHTML(procurementPlanningDataById?.payload?.field_procurement_description_ms?.value)
        : decodeHTML(procurementPlanningData?.[0]?.field_procurement_description_ms),
      field_procurement_description_zh: procurementPlanningDataById?.payload
        ? decodeHTML(procurementPlanningDataById?.payload?.field_procurement_description_zh?.value)
        : decodeHTML(procurementPlanningData?.[0]?.field_procurement_description_zh),
      field_procurement_video_url: procurementPlanningDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "video_url.mp4",
              status: "done",
              url: procurementPlanningDataById?.payload?.field_procurement_video_url,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "video_url.mp4",
              status: "done",
              url: procurementPlanningData?.[0]?.field_procurement_video_url,
            },
          ]),
      field_procure_details_web_image: procurementPlanningDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_procure_details_web_image.png",
              status: "done",
              url: procurementPlanningDataById?.payload?.field_procure_details_web_image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_procure_details_web_image.png",
              status: "done",
              url: procurementPlanningData?.[0]?.field_procure_details_web_image,
            },
          ]),
      field_procurement_web_flowchart: procurementPlanningDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "field_procurement_web_flowchart.png",
              status: "done",
              url: procurementPlanningDataById?.payload?.field_procurement_web_flowchart,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "field_procurement_web_flowchart.png",
              status: "done",
              url: procurementPlanningData?.[0]?.field_procurement_web_flowchart,
            },
          ]),
    },
    fields: fields.procurementPlanning,
  };

  useEffect(() => {
    const fetchTabsData = async () => {
      try {
        const response = await magpCMSApi.get(`/api/procurement-planning-list/all-fields`);

        const tabData = response.data.map((tab: any) => {
          const listId = tab?.id;
          const label = tab?.field_process_name_en.replace(/^\d+\.\d+\s*/, "");
          let key = label.replace(/\s+/g, "").replace(/\s[a-z]/g, (match: any) => match.trim().toUpperCase());
          key = key.charAt(0).toLowerCase() + key.slice(1);
          return { listId, label, key };
        });

        // tabData.push({
        //   listId: "",
        //   label: "Process Flow Chart",
        //   key: "procurementPlanningProcessFlowChart"
        // });

        setTabsData(tabData);
      } catch (error) {}
    };

    fetchTabsData();
    const subActiveTab: string = query.get("subActiveTab") ?? "businessCase";
    const listId = subActiveTab === "businessCase" ? "15" : subActiveTab === "annualProcurementPlanning" ? "16" : "17";
    fetchSubTabsData(subActiveTab, listId);
  }, []);

  useEffect(() => {
    if (procurementPlanningDataById !== undefined && !isLoadingProcurementPlanning) {
      const urlParams = new URLSearchParams(window.location.search);
      const subActiveTab = urlParams.get("subActiveTab") || "businessCase";
      const selectedTab = tabsData.find((tab) => tab.key === subActiveTab);
      if (selectedTab !== undefined) {
        fetchSubTabsData(subActiveTab, selectedTab.listId);
      }
    }
  }, [procurementPlanningDataById, tabsData, isLoadingProcurementPlanning]);

  const fetchSubTabsData = async (activeKey: string, listId: string) => {
    if (!listId) {
      return;
    }
    try {
      const response = await magpCMSApi.get(`/api/procurement-topbar-toggles/all-fields?listId=${listId}`);

      const subTabData = response.data
        // .filter((subTab: any) => !subTab.name.includes("Process Flow Chart"))
        .map((subTab: any) => {
          const formattedName = subTab.name
            .replace(/\s+/g, "")
            .replace(/(\s[a-z])/g, (match: string) => match.trim().toUpperCase())
            .replace(/[^a-zA-Z0-9]/g, "");

          return {
            toggleId: subTab.id,
            listId: subTab.field_procurement_list_reference,
            label: subTab.name,
            key: `${activeKey}${formattedName}`,
          };
        });

      setSubTabsData(subTabData);
    } catch (error) {}
  };

  const handleTabChange = (activeKey: string) => {
    const selectedTab = tabsData.find((tab) => tab.key === activeKey);
    if (selectedTab) {
      fetchSubTabsData(activeKey, selectedTab.listId);
      navigate(`${window.location.pathname}?activeTab=procurementPlanning&subActiveTab=${activeKey}`);
    }
  };

  const handleSubTabChange = (listId: string, activeKey: string) => {
    setListId(listId);
    const subTab = subTabsData.find((subTab) => subTab.key === activeKey);
    if (subTab) {
      setToggleId(subTab.toggleId);
    }
    navigate(
      `${window.location.pathname}?activeTab=procurementPlanning&subActiveTab=${query?.get(
        "subActiveTab"
      )}&thirdChildActiveTab=${activeKey}`
    );
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    let imageKeys: any = [
      "field_procure_details_web_image",
      "field_procurement_video_url",
      "field_procurement_web_flowchart",
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field && field[0]) {
          if (field[0].originFileObj) {
            const formData = new FormData();
            formData.append("upload-file", field[0].originFileObj);
            formData.append("application", "MAGP");
            const res: any = await post("management/upload-file", formData);

            if (res) {
              data[key] = res.response.data;
            }
          } else {
            data[key] = data[key][0].url;
          }
        }
      } catch (error) {}
    }
    let descKeys: any = [
      "field_procurement_description_en",
      "field_procurement_description_ms",
      "field_procurement_description_zh",
    ];
    for (const key of descKeys) {
      try {
        data[key] = {
          value: data[key] || "",
          format: "full_html",
        };
      } catch (error) {}
    }
    const payload = {
      action: action,
      method: method,
      module: "Manual",
      subModule: `${query?.get("activeTab")}_${query?.get("subActiveTab")}_${query?.get("thirdChildActiveTab")}`,
      type: "DEDICATED",
      application: "MAGP",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAGP",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        id: data?.id,
        field_procure_planning_parent_id: procurementPlanningData?.[0]?.field_procure_planning_parent_id || listId,
        field_procurement_toggle_referen: procurementPlanningData?.[0]?.field_procurement_toggle_referen || toggleId,
        ...data,
        published: true,
        // createdAt: "2024-07-16T07:44:25",
        // updatedAt: "2024-07-16T07:44:30",
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          refetchData();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          refetchData();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  const superSubChildItems = tabsData.map((tab: any) => {
    const isProcessFlowChartTab = tab.key === "procurementPlanningProcessFlowChart";

    return {
      label: tab.label,
      key: tab.key,
      children: isProcessFlowChartTab ? (
        <>
          {" "}
          <ProcurementPlanningProcessFlowChart
            listId="15"
            toggleId="129"
          />
        </>
      ) : (
        <Tabs
          activeKey={query?.get("thirdChildActiveTab") || ""}
          items={subTabsData.map((subTab: any) => {
            const processFlowChartTab: any[] = [
              "businessCaseProcessFlowChart",
              "strategyPaperProcessFlowChartOpenTenderInvitationToBid",
              "strategyPaperProcessFlowChartDirectAppointment",
              "annualProcurementPlanningProcessFlowChart",
              "procurementPlanningProcessFlowChart",
            ];
            const isProcessFlowChartTab = processFlowChartTab.includes(subTab.key);

            return {
              label: subTab.label,
              key: subTab.key,
              children: isProcessFlowChartTab ? (
                <>
                  <DynamicForm
                    isLoading={isLoadingProcurementPlanning || isLoadingProcurementPlanningById}
                    config={ProcurementPlanningFormConfig}
                    onSubmit={(data, buttonValue, reviewNotes) =>
                      onSubmit(
                        data,
                        `${magpHost}/api/procurement-planning-details/${procurementPlanningData?.[0]?.id}`,
                        "UPDATE",
                        "PATCH",
                        buttonValue === "save" ? "DRAFT" : "PENDING",
                        reviewNotes
                      )
                    }
                  />
                  <ProcurementPlanningProcessFlowChart
                    listId={listId}
                    toggleId={toggleId}
                  />
                </>
              ) : (
                <DynamicForm
                  isLoading={isLoadingProcurementPlanning || isLoadingProcurementPlanningById}
                  config={ProcurementPlanningFormConfig}
                  onSubmit={(data, buttonValue, reviewNotes) =>
                    onSubmit(
                      data,
                      `${magpHost}/api/procurement-planning-details/${procurementPlanningData?.[0]?.id}`,
                      "UPDATE",
                      "PATCH",
                      buttonValue === "save" ? "DRAFT" : "PENDING",
                      reviewNotes
                    )
                  }
                />
              ),
            };
          })}
          onChange={(activeKey: any) => handleSubTabChange(tab.listId, activeKey)}
        />
      ),
    };
  });

  if (isLoadingProcurementPlanning || isLoadingProcurementPlanningById) {
    return <Loader />;
  }

  return (
    <Tabs
      activeKey={query?.get("subActiveTab") || ""}
      items={superSubChildItems}
      onChange={handleTabChange}
    />
  );
};
